/**
 * Pure Plotly chart builder for the MARS Portfolio Allocations chart.
 *
 * No React — this turns the wide timeseries payload plus toggle/selection
 * state into a ready-to-render `{ data, layout }` pair. `ExposuresSection.tsx`
 * composes it inside a `useMemo`.
 */
import type { Data, Layout } from 'plotly.js';

import { hoverTemplate } from '@/lib/plotly/hoverTemplates';
import { chartLayout } from '@/lib/plotly/layoutPresets';
import { AXIS_GRID, AXIS_LINE } from '@/lib/plotly/defaults';
import { getRecessionShapes } from '@/lib/plotly/recessionShapes';
import { ASSET_COLORS_WITH_ALIASES as ASSET_COLORS, hexToRgba } from '@/lib/plotly/colors';

import { ASSET_DATA_KEYS, getExposureTickConfig, sliceByHorizon } from './utils';
import type { ExposureSeries, Fund, TimeseriesPayload, Timeframe } from '../types';

export interface BuiltExposuresPlot {
  data: Data[];
  layout: Partial<Layout>;
}

const EMPTY_PLOT: BuiltExposuresPlot = { data: [], layout: {} };

const DEFAULT_FIRST_ASSET = 'Equities';

export interface ExposureToggles {
  volTiming: boolean;
  constraints: boolean;
  tc: boolean;
}

/**
 * Pick the right exposures branch from the wide timeseries payload based on
 * toggle state (execution_id selection is done server-side; this chooses the
 * correct key the server returned).
 */
export function pickDataSource(
  fundId: string,
  data: TimeseriesPayload,
  toggles: ExposureToggles,
): { dataSource: ExposureSeries | undefined; overlay: ExposureSeries | undefined } {
  const baseExposures = data.exposures;
  const baseWeights = data.weights;
  const { volTiming, constraints, tc } = toggles;

  let dataSource: ExposureSeries | undefined;

  if (fundId === 'mars-fidelity-balanced' || fundId === 'mars-fidelity-conservative') {
    if (!volTiming && !tc) dataSource = data.exposures_no_sv_no_tc ?? baseWeights;
    else if (!tc) dataSource = data.exposures_volatility_only ?? baseExposures;
    else dataSource = baseExposures;
  } else if (fundId === 'mars-dar-nn') {
    if (!volTiming) dataSource = data.exposures_no_tc_no_vol_timing ?? baseWeights;
    else if (!constraints) dataSource = data.exposures_no_tc_no_constraint ?? baseWeights;
    else if (!tc) dataSource = data.exposures_no_tc ?? baseExposures;
    else dataSource = baseExposures;
  } else if (!volTiming && !tc) {
    dataSource = data.exposures_no_sv_no_tc ?? baseWeights;
  } else if (!tc) {
    dataSource = data.exposures_volatility_only ?? baseExposures;
  } else {
    dataSource = baseExposures;
  }

  const overlay = !volTiming || !tc ? baseExposures : undefined;
  return { dataSource, overlay };
}

/** Chart title — Fidelity funds drop the "(TC …, SV …)" toggle-state suffix. */
export function buildTitle(fund: Fund, toggles: { volTiming: boolean; tc: boolean }): string {
  const base = 'Portfolio Allocations';
  if (fund.id.startsWith('mars-fidelity-')) return base;
  return `${base} (TC ${toggles.tc ? 'on' : 'off'}, SV ${toggles.volTiming ? 'on' : 'off'})`;
}

/** Scale a fractional series (0.15) to percentage points (15), keeping nulls. */
function toPercent(values: Array<number | null | undefined>): Array<number | null> {
  return values.map((v) => (v === null || v === undefined ? null : v * 100));
}

/** One coloured allocation line per selected asset, plus a faint "Actual Traded" overlay. */
function buildAssetTraces(
  ordered: string[],
  dataSource: ExposureSeries,
  overlay: ExposureSeries | undefined,
  timeframe: Timeframe,
): Data[] {
  const traces: Data[] = [];

  ordered.forEach((assetName) => {
    const dataKey = ASSET_DATA_KEYS[assetName] ?? assetName;
    const values = dataSource.series?.[dataKey];
    if (!values) return;

    const sliced = sliceByHorizon(dataSource.dates, [values], timeframe);
    const color = ASSET_COLORS[assetName] ?? '#666';

    traces.push({
      type: 'scatter',
      mode: 'lines',
      name: assetName,
      x: sliced.dates,
      y: toPercent(sliced.series[0]),
      line: { color, width: 3 },
      hovertemplate: hoverTemplate({ name: assetName, format: '.1f', suffix: '%' }),
    });

    // Faint "Actual Traded" overlay — drawn when a toggle is off but the base
    // exposures branch is still available.
    if (overlay?.series?.[dataKey]) {
      const ovSliced = sliceByHorizon(overlay.dates, [overlay.series[dataKey]], timeframe);
      traces.push({
        type: 'scatter',
        mode: 'lines',
        name: `${assetName} (Actual Traded)`,
        x: ovSliced.dates,
        y: toPercent(ovSliced.series[0]),
        line: { color: hexToRgba(color, 0.5), width: 2 },
        showlegend: false,
        hoverinfo: 'skip',
      });
    }
  });

  return traces;
}

/** A flat horizontal line per asset at its benchmark/strategic level. */
function buildReferenceLines(
  ordered: string[],
  levels: Record<string, number>,
  xRange: [string, string],
  kind: 'Benchmark' | 'Strategic',
): Data[] {
  const dash = kind === 'Benchmark' ? 'dash' : 'dot';
  const opacity = kind === 'Benchmark' ? 0.6 : 0.5;

  return ordered.flatMap((assetName) => {
    const key = ASSET_DATA_KEYS[assetName] ?? assetName;
    const level = levels[key];
    if (level === undefined || level === null) return [];
    return [
      {
        type: 'scatter',
        mode: 'lines',
        name: `${assetName} ${kind}`,
        x: xRange,
        y: [level, level],
        line: { color: hexToRgba(ASSET_COLORS[assetName] ?? '#666', opacity), width: 2, dash },
        showlegend: true,
        hovertemplate: `${assetName} ${kind}: ${typeof level === 'number' ? level.toFixed(1) : level}%<extra></extra>`,
        hoverinfo: 'skip',
      } as Data,
    ];
  });
}

/**
 * X-axis range/tick settings derived from the data's actual time span.
 *
 * The range start sits at the FIRST data point (not snapped to the month
 * start) — snapping leaves a multi-day white gap on the left whenever the
 * first point isn't on the 1st. `tick0` stays pinned to the month start so
 * subsequent ticks line up; the range end is extended one month past the
 * last data point.
 */
function deriveXAxis(firstX: string[] | undefined): {
  tickConfig: { tickformat: string; dtick: string };
  range?: [string, string];
  tick0?: string;
} {
  if (!firstX || firstX.length <= 1) {
    return { tickConfig: { tickformat: '%b-%y', dtick: 'M2' } };
  }

  const firstDate = new Date(firstX[0]);
  const lastDate = new Date(firstX[firstX.length - 1]);
  const years = (lastDate.getTime() - firstDate.getTime()) / (365.25 * 24 * 3600 * 1000);

  const extendedDate = new Date(
    lastDate.getFullYear(),
    lastDate.getMonth() + 1,
    lastDate.getDate(),
  );
  const tick0 = `${firstDate.getUTCFullYear()}-${String(firstDate.getUTCMonth() + 1).padStart(2, '0')}-01`;

  return {
    tickConfig: getExposureTickConfig(years),
    range: [firstX[0], extendedDate.toISOString().slice(0, 10)],
    tick0,
  };
}

/** Build the full Portfolio Allocations `{ data, layout }`. */
export function buildExposuresPlot(
  fund: Fund,
  payload: TimeseriesPayload,
  selectedAssets: string[],
  timeframe: Timeframe,
  toggles: ExposureToggles,
): BuiltExposuresPlot {
  const { dataSource, overlay } = pickDataSource(fund.id, payload, toggles);
  if (!dataSource?.dates?.length) return EMPTY_PLOT;

  const ordered = selectedAssets.length > 0 ? selectedAssets : [DEFAULT_FIRST_ASSET];
  const traces = buildAssetTraces(ordered, dataSource, overlay, timeframe);

  // Reference lines span the x-extent of the first asset trace.
  const firstX = (traces[0] as { x?: unknown })?.x as string[] | undefined;
  const xRange: [string, string] | null =
    firstX && firstX.length > 0 ? [firstX[0], firstX[firstX.length - 1]] : null;

  if (xRange && fund.has_benchmark && payload.benchmark) {
    traces.push(...buildReferenceLines(ordered, payload.benchmark, xRange, 'Benchmark'));
  }
  if (xRange && payload.steady_state_strategic) {
    traces.push(
      ...buildReferenceLines(ordered, payload.steady_state_strategic, xRange, 'Strategic'),
    );
  }

  const xAxis = deriveXAxis(firstX);

  const layout: Partial<Layout> = {
    // Convention: title at top, legend BETWEEN title and chart drawing area,
    // inner ticks. The margin override tightens spacing for this chart.
    ...chartLayout({
      family: 'timeseries',
      marginOverride: { l: 40, r: 20, t: 85, b: 35, pad: 0 },
    }),
    title: {
      text: buildTitle(fund, toggles),
      font: { size: 18 },
      y: 0.98,
      yanchor: 'top',
    },
    legend: {
      orientation: 'h',
      x: 0.5,
      xanchor: 'center',
      y: 1.02,
      yanchor: 'bottom',
      font: { size: 16 },
    },
    xaxis: {
      type: 'date',
      tickformat: xAxis.tickConfig.tickformat,
      dtick: xAxis.tickConfig.dtick,
      ...(xAxis.tick0 ? { tick0: xAxis.tick0 } : {}),
      ...AXIS_GRID,
      ...AXIS_LINE,
      ticks: 'inside',
      ticklen: 8,
      tickwidth: 1,
      tickcolor: '#000000',
      ...(xAxis.range ? { range: xAxis.range } : {}),
    },
    yaxis: {
      ticksuffix: '%',
      tickformat: '.0f',
      ...AXIS_GRID,
      ...AXIS_LINE,
      ticks: 'inside',
      ticklen: 8,
      tickwidth: 1,
      tickcolor: '#000000',
    },
    shapes: xRange ? getRecessionShapes({ plotXRange: [xRange[0], xRange[1]] }) : [],
  };

  return { data: traces, layout };
}
